import { useAppStore } from "@/store";
import { AppStore } from "@/store/AppStore";
import { TopicInfo } from "@/components/item-list/types";
import { removeNoteFromCollection } from "@/actions/removeNoteFromCollection";
import { observer } from "mobx-react-lite";
import { INoteObservable, NotesIndexTuple } from "@/store/note";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { ReactEventHandler } from "react";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { useLiveQuery } from "dexie-react-hooks";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { CollectionPreviewView } from "./collection-preview.view";

function getItem(
  store: AppStore,
  noteId?: string,
  templateId?: string
): INoteObservable | TemplateObservable | undefined {
  if (noteId) return store.notes.getNoteObservableById({ noteId });
  if (templateId) return store.templates.get(templateId);
  return undefined;
}

interface CollectionPreviewProps {
  collectionTopic: TopicInfo;
  noteId?: string;
  templateId?: string;
  onDismiss: () => void;
}

export const CollectionPreview = observer(
  ({ collectionTopic, noteId, templateId, onDismiss }: CollectionPreviewProps) => {
    const { store } = useAppStore();
    const tabContext = useMaybeTabContext();

    const collection = store.collections.getCollectionObservableById({
      collectionId: collectionTopic.id,
    });

    const item = getItem(store, noteId, templateId);

    const recentNoteResults = useLiveQuery(() =>
      store.notes.search({
        lens: LensKind.All,
        sortBy: SortByKind.LastInteracted,
        limit: 5,
        filter: { collections: [{ ids: [collectionTopic.id], mode: "all" }] },
      })
    );
    const recentNotes = store.notes.getMany(
      recentNoteResults?.map((result: NotesIndexTuple) => result.at(-1) as string) ?? []
    );

    const handleRemove: React.MouseEventHandler = e => {
      if (!item || !collection) return;
      e.stopPropagation();
      removeNoteFromCollection({
        note: item,
        collection,
        store,
      });
      return true;
    };

    const handleOpenInSplit: ReactEventHandler = e => {
      if (!collection) return;
      e.stopPropagation();
      store.navigation.goToCollection({
        collectionId: collection.id,
        openInInactivePane: true,
        activeTabId: tabContext?.activeTab.id,
      });

      return true;
    };

    const handleOpenNote = (note: INoteObservable) => {
      if (!note) return;
      onDismiss();
      store.navigation.goToNote({ noteId: note.id });
    };

    if (!collection) return null;
    return (
      <CollectionPreviewView
        collectionId={collection.id}
        collectionLabel={collection.label}
        collectionDescription={collection.description}
        recentNotes={recentNotes.map(note => ({
          id: note.id,
          title: note.title,
        }))}
        noteId={noteId ?? templateId}
        onRemove={handleRemove}
        onOpenInSplit={handleOpenInSplit}
        onOpenNote={noteId => {
          const note = store.notes.getNoteObservableById({ noteId });
          if (note) handleOpenNote(note);
        }}
      />
    );
  }
);
